import React, { useState, useEffect, useCallback } from 'react';

/* Standalone-safe apiFetch (matches DataManagement.jsx) */
async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('bizsuite_token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!options.headers?.['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { ...options, headers, credentials: 'include' });
  if (res.status === 401) {
    localStorage.removeItem('bizsuite_token');
    window.location.href = '/login.html';
    throw new Error('Unauthorized');
  }
  return res;
}

const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'integer', label: 'Integer' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Boolean' },
];

const styles = {
  container: { padding: '24px 0' },
  header: { marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  title: { fontSize: 22, fontWeight: 700, color: '#E2E8F0', margin: 0 },
  subtitle: { fontSize: 13, color: '#64748B', margin: '4px 0 0 0' },

  card: {
    background: 'rgba(30,41,59,0.7)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  tableHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1.6fr 90px 110px 280px',
    padding: '10px 14px',
    background: 'rgba(15,23,42,0.6)',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    fontSize: 11,
    fontWeight: 700,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1.6fr 90px 110px 280px',
    padding: '12px 14px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    alignItems: 'center',
    fontSize: 13,
  },
  tableLink: { color: '#60A5FA', cursor: 'pointer', textDecoration: 'none' },
  count: {
    fontSize: 11,
    color: '#94A3B8',
    background: 'rgba(100,116,139,0.18)',
    padding: '2px 8px',
    borderRadius: 999,
    width: 'max-content',
  },

  // Wizard
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
  },
  modal: {
    background: '#0F172A', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14,
    width: '100%', maxWidth: 880, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  },
  modalHeader: {
    padding: '16px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#E2E8F0', margin: 0 },
  modalBody: { padding: 22, overflow: 'auto', flex: 1 },
  modalFooter: {
    padding: '14px 22px', borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
  },

  steps: { display: 'flex', gap: 8, marginBottom: 20 },
  step: {
    flex: 1, padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
    textAlign: 'center', background: 'rgba(30,41,59,0.6)', color: '#64748B',
    border: '1px solid rgba(255,255,255,0.04)',
  },
  stepActive: { background: 'rgba(59,130,246,0.2)', color: '#93C5FD', borderColor: 'rgba(59,130,246,0.5)' },
  stepDone: { background: 'rgba(16,185,129,0.15)', color: '#6EE7B7', borderColor: 'rgba(16,185,129,0.4)' },

  field: { marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#CBD5E1', marginBottom: 6 },
  input: {
    width: '100%', padding: '8px 10px', fontSize: 13, background: 'rgba(15,23,42,0.6)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#E2E8F0',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '6px 8px', fontSize: 12, background: 'rgba(15,23,42,0.6)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, color: '#E2E8F0',
  },
  fileDrop: {
    border: '2px dashed rgba(148,163,184,0.3)', borderRadius: 10,
    padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
    background: 'rgba(15,23,42,0.5)', transition: 'background 0.15s',
  },
  fileDropActive: { background: 'rgba(59,130,246,0.08)', borderColor: 'rgba(59,130,246,0.5)' },

  colsTable: {
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'hidden',
    fontSize: 12,
  },
  colsTableHeader: {
    display: 'grid', gridTemplateColumns: '60px 2fr 2fr 120px 80px',
    padding: '8px 10px', background: 'rgba(15,23,42,0.6)',
    fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4,
  },
  colsTableRow: {
    display: 'grid', gridTemplateColumns: '60px 2fr 2fr 120px 80px',
    padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,0.04)', alignItems: 'center', gap: 6,
  },

  sample: {
    marginTop: 16, fontSize: 11,
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, overflow: 'auto', maxHeight: 240,
  },
  errorBanner: {
    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)',
    color: '#FCA5A5', padding: '10px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13,
  },
  successBanner: {
    background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)',
    color: '#6EE7B7', padding: '10px 14px', borderRadius: 6, marginBottom: 14, fontSize: 13,
  },

  rowActions: { display: 'flex', gap: 6, justifyContent: 'flex-end' },
  iconBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#94A3B8',
    padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 11,
  },
  loading: { textAlign: 'center', padding: 40, color: '#64748B' },
};

function CreateTableWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1: file pick, 2: preview/columns, 3: result
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { headers, columns, sample, totalRows, sheetNames, sheetName, suggestedTableName }
  const [columns, setColumns] = useState([]);
  const [tableName, setTableName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [importData, setImportData] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onPickFile = async (f) => {
    if (!f) return;
    setFile(f);
    setError(null);
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('originalFilename', f.name);
      const res = await apiFetch('/api/admin/custom-tables/preview', {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Preview failed');
      setPreview(data);
      setColumns(data.columns || []);
      setTableName(data.suggestedTableName || '');
      setDisplayName(data.suggestedTableName || '');
      setStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setPreviewing(false);
    }
  };

  const updateColumn = (idx, patch) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('originalFilename', file.name);
      fd.append('displayName', displayName || tableName);
      fd.append('tableName', tableName);
      fd.append('description', description || '');
      fd.append('columns', JSON.stringify(columns));
      fd.append('importData', importData ? 'true' : 'false');
      const res = await apiFetch('/api/admin/custom-tables/create-from-file', {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Create failed');
      setResult(data);
      setStep(3);
      onCreated && onCreated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const stepStyle = (n) => ({
    ...styles.step,
    ...(step === n ? styles.stepActive : step > n ? styles.stepDone : {}),
  });

  return (
    <div style={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Create Custom Table</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.steps}>
            <div style={stepStyle(1)}>1 &nbsp; Upload File</div>
            <div style={stepStyle(2)}>2 &nbsp; Review Columns</div>
            <div style={stepStyle(3)}>3 &nbsp; Done</div>
          </div>

          {error && <div style={styles.errorBanner}>{error}</div>}

          {step === 1 && (
            <div>
              <label
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  const f = e.dataTransfer.files && e.dataTransfer.files[0];
                  if (f) onPickFile(f);
                }}
                style={{ ...styles.fileDrop, ...(dragActive ? styles.fileDropActive : {}) }}
              >
                <div style={{ fontSize: 14, fontWeight: 600, color: '#CBD5E1', marginBottom: 6 }}>
                  Drop an Excel file here, or click to browse
                </div>
                <div style={{ fontSize: 12, color: '#64748B' }}>
                  .xlsx / .xls — max 200 MB.
                  Headers are read from row 1; data starts at row 2.
                </div>
                {previewing && <div style={{ marginTop: 12, color: '#93C5FD' }}>Reading file...</div>}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={(e) => onPickFile(e.target.files && e.target.files[0])}
                />
              </label>
            </div>
          )}

          {step === 2 && preview && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={styles.field}>
                  <label style={styles.label}>Display Name</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Table Name (auto-sanitized)</label>
                  <input
                    type="text"
                    style={styles.input}
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  />
                </div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Description (optional)</label>
                <input
                  type="text"
                  style={styles.input}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={`Created from ${file ? file.name : 'upload'}`}
                />
              </div>

              <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 8 }}>
                Sheet: <strong style={{ color: '#CBD5E1' }}>{preview.sheetName}</strong> &nbsp;·&nbsp;
                {preview.totalRows.toLocaleString()} data rows
              </div>

              <div style={styles.colsTable}>
                <div style={styles.colsTableHeader}>
                  <span>#</span>
                  <span>Column Name</span>
                  <span>Header (Excel)</span>
                  <span>Type</span>
                  <span style={{ textAlign: 'center' }}>Include</span>
                </div>
                {columns.map((c, i) => (
                  <div key={i} style={styles.colsTableRow}>
                    <span style={{ color: '#64748B' }}>{i + 1}</span>
                    <input
                      type="text"
                      style={{ ...styles.input, padding: '4px 6px', fontSize: 12 }}
                      value={c.name}
                      onChange={(e) => updateColumn(i, { name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    />
                    <span style={{ color: '#94A3B8', fontSize: 12 }}>{c.label}</span>
                    <select
                      style={styles.select}
                      value={c.type || 'text'}
                      onChange={(e) => updateColumn(i, { type: e.target.value })}
                    >
                      {TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <input
                      type="checkbox"
                      checked={c.included !== false}
                      onChange={(e) => updateColumn(i, { included: e.target.checked })}
                      style={{ justifySelf: 'center' }}
                    />
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 14, fontSize: 12, color: '#94A3B8' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={importData}
                    onChange={(e) => setImportData(e.target.checked)}
                  />
                  Import data immediately ({preview.totalRows.toLocaleString()} rows)
                </label>
              </div>

              {preview.sample && preview.sample.length > 0 && (
                <div style={styles.sample}>
                  <div style={{ padding: '8px 10px', background: 'rgba(15,23,42,0.6)', fontWeight: 700, fontSize: 11, color: '#94A3B8' }}>
                    Sample ({Math.min(preview.sample.length, 10)} rows)
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {(preview.headers || []).map((h, i) => (
                          <th key={i} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#CBD5E1', fontSize: 11 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sample.slice(0, 10).map((row, ri) => (
                        <tr key={ri}>
                          {(preview.headers || []).map((h, ci) => (
                            <td key={ci} style={{ padding: '6px 8px', borderBottom: '1px solid rgba(255,255,255,0.03)', color: '#E2E8F0', fontSize: 11 }}>
                              {row[h] === null || row[h] === undefined ? '' : String(row[h])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {step === 3 && result && (
            <div style={styles.successBanner}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Table created</div>
              <div>{result.message}</div>
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8 }}>
                Table: <code>{result.tableName}</code> &nbsp;·&nbsp; Columns: {result.columns} &nbsp;·&nbsp; Rows imported: {result.inserted || 0}
              </div>
            </div>
          )}
        </div>

        <div style={styles.modalFooter}>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            {step === 1 && 'Step 1 of 3 — pick a file'}
            {step === 2 && 'Step 2 of 3 — review and adjust'}
            {step === 3 && 'Step 3 of 3 — finished'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {step === 2 && (
              <button className="btn btn-secondary btn-sm" onClick={() => setStep(1)} disabled={submitting}>
                Back
              </button>
            )}
            {step < 3 && step === 2 && (
              <button
                className="btn btn-primary btn-sm"
                onClick={submit}
                disabled={submitting || !columns.length || !tableName}
              >
                {submitting ? 'Creating...' : (importData ? 'Create & Import' : 'Create Table')}
              </button>
            )}
            {step === 3 && (
              <button className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditTableModal({ table, onClose, onSaved }) {
  const [displayName, setDisplayName] = useState(table.display_name || table.table_name);
  const [description, setDescription] = useState(table.description || '');
  const [columns, setColumns] = useState(() => (table.columns || []).map((c) => ({ ...c })));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const updateColumn = (idx, patch) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/admin/custom-tables/${table.table_name}`, {
        method: 'PATCH',
        body: JSON.stringify({
          displayName,
          description,
          columns: columns.map((c) => ({ name: c.name, label: c.label })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Edit Table — {table.table_name}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        <div style={styles.modalBody}>
          {error && <div style={styles.errorBanner}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={styles.field}>
              <label style={styles.label}>Display Name</label>
              <input
                type="text"
                style={styles.input}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Table Name (read-only)</label>
              <input
                type="text"
                style={{ ...styles.input, opacity: 0.6, cursor: 'not-allowed' }}
                value={table.table_name}
                disabled
              />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Description</label>
            <input
              type="text"
              style={styles.input}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div style={{ marginTop: 8, marginBottom: 8, fontSize: 12, color: '#94A3B8' }}>
            Columns &mdash; only labels can be edited here. To change column names, types,
            or to add/remove columns, delete the table and recreate it.
          </div>
          <div style={styles.colsTable}>
            <div style={styles.colsTableHeader}>
              <span>#</span>
              <span>Column Name</span>
              <span>Label</span>
              <span>Type</span>
              <span></span>
            </div>
            {columns.map((c, i) => (
              <div key={i} style={styles.colsTableRow}>
                <span style={{ color: '#64748B' }}>{i + 1}</span>
                <code style={{ color: '#94A3B8', fontSize: 12 }}>{c.name}</code>
                <input
                  type="text"
                  style={{ ...styles.input, padding: '4px 6px', fontSize: 12 }}
                  value={c.label || ''}
                  onChange={(e) => updateColumn(i, { label: e.target.value })}
                />
                <span style={{ color: '#94A3B8', fontSize: 12 }}>{c.type}</span>
                <span></span>
              </div>
            ))}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            {columns.length} column{columns.length === 1 ? '' : 's'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={submitting}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UploadDataModal({ table, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('append');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mode', mode);
      const res = await apiFetch(`/api/admin/custom-tables/${table.table_name}/upload-data`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setResult(data);
      onUploaded && onUploaded();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...styles.modal, maxWidth: 540 }}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Upload Data — {table.display_name || table.table_name}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        <div style={styles.modalBody}>
          {error && <div style={styles.errorBanner}>{error}</div>}
          {result && (
            <div style={styles.successBanner}>
              {result.message || `${result.inserted} rows imported`}
            </div>
          )}
          <div style={styles.field}>
            <label style={styles.label}>File</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files && e.target.files[0])}
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Mode</label>
            <label style={{ display: 'flex', gap: 12, fontSize: 13, color: '#CBD5E1' }}>
              <span><input type="radio" checked={mode === 'append'} onChange={() => setMode('append')} /> Append</span>
              <span><input type="radio" checked={mode === 'overwrite'} onChange={() => setMode('overwrite')} /> Overwrite (delete + load)</span>
            </label>
          </div>
        </div>
        <div style={styles.modalFooter}>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            {file ? file.name : 'No file selected'}
          </div>
          <div>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={submitting || !file}>
              {submitting ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CustomTables() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [uploadFor, setUploadFor] = useState(null);
  const [editFor, setEditFor] = useState(null);

  const fetchTables = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch('/api/admin/custom-tables');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setTables(data.tables || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTables(); }, [fetchTables]);

  const downloadTemplate = async (t) => {
    try {
      const res = await fetch(`/api/admin/custom-tables/${t.table_name}/template-data`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('bizsuite_token')}` },
      });
      if (!res.ok) throw new Error('Template download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t.table_name}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message);
    }
  };

  const removeTable = async (t) => {
    if (!confirm(`Delete table "${t.display_name || t.table_name}"?\n\nThis will drop the underlying Postgres table.`)) return;
    try {
      const res = await apiFetch(`/api/admin/custom-tables/${t.table_name}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      fetchTables();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Custom Tables</h2>
          <p style={styles.subtitle}>
            Upload an Excel file to create a new Postgres table. Columns and types are inferred automatically and you can adjust them before creating.
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowWizard(true)}>
          + New Custom Table
        </button>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.card}>
        <div style={styles.tableHeaderRow}>
          <span>Display Name</span>
          <span>Table Name</span>
          <span>Rows</span>
          <span>Created</span>
          <span style={{ textAlign: 'right' }}>Actions</span>
        </div>

        {loading ? (
          <div style={styles.loading}>Loading custom tables...</div>
        ) : tables.length === 0 ? (
          <div style={styles.loading}>
            No custom tables yet. Click <strong>New Custom Table</strong> to create one from an Excel file.
          </div>
        ) : (
          tables.map((t) => (
            <div key={t.id} style={styles.tableRow}>
              <span style={{ color: '#E2E8F0', fontWeight: 600 }}>{t.display_name || t.table_name}</span>
              <code style={{ color: '#94A3B8', fontSize: 12 }}>{t.table_name}</code>
              <span style={styles.count}>
                {t.rowCount == null ? '—' : Number(t.rowCount).toLocaleString()}
              </span>
              <span style={{ color: '#64748B', fontSize: 12 }}>
                {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
              </span>
              <div style={styles.rowActions}>
                <button style={styles.iconBtn} onClick={() => setEditFor(t)} title="Edit table metadata">Edit</button>
                <button style={styles.iconBtn} onClick={() => downloadTemplate(t)} title="Download empty template">Template</button>
                <button style={styles.iconBtn} onClick={() => setUploadFor(t)} title="Upload data">Upload</button>
                <button style={{ ...styles.iconBtn, color: '#FCA5A5' }} onClick={() => removeTable(t)} title="Drop table">Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {showWizard && (
        <CreateTableWizard
          onClose={() => setShowWizard(false)}
          onCreated={fetchTables}
        />
      )}

      {uploadFor && (
        <UploadDataModal
          table={uploadFor}
          onClose={() => setUploadFor(null)}
          onUploaded={fetchTables}
        />
      )}

      {editFor && (
        <EditTableModal
          table={editFor}
          onClose={() => setEditFor(null)}
          onSaved={fetchTables}
        />
      )}
    </div>
  );
}
